/**
 * Client-side outbound-auth-email guard.
 *
 * Firebase sends verification and password-reset email from Google's servers,
 * so the server-side EMAIL_REDIRECT_TO guard cannot intercept it. The only
 * place to stop it is before the address is handed to Firebase.
 *
 * VITE_AUTH_EMAIL_ALLOWLIST (env):
 *   - When set to a comma-separated list of substrings (e.g. "@gofranchise.io"),
 *     any Firebase call that would create an account for — or email — an address
 *     that matches none of them is refused before it leaves the browser.
 *   - When empty/unset, this is a no-op passthrough. Leave it unset in
 *     production; set it in any environment pointed at live Firebase/DB data so
 *     a test signup can never email a real user.
 *
 * This deliberately guards only account creation and email-sending. Plain
 * sign-in is never blocked — it sends nothing, and blocking it would prevent
 * reproducing login bugs on real accounts.
 */

function getAllowlist(): string[] | null {
  const raw = (import.meta.env.VITE_AUTH_EMAIL_ALLOWLIST as string | undefined)?.trim();
  if (!raw) return null;
  const entries = raw
    .split(',')
    .map((entry) => entry.trim().toLowerCase())
    .filter(Boolean);
  return entries.length > 0 ? entries : null;
}

export function isAuthEmailGuardActive(): boolean {
  return getAllowlist() !== null;
}

export function isAuthEmailAllowed(email: string): boolean {
  const allowlist = getAllowlist();
  if (!allowlist) return true;
  const normalized = email.trim().toLowerCase();
  return allowlist.some((entry) => normalized.includes(entry));
}

/**
 * Throws when the guard is active and `email` is not allowlisted. Call this
 * before any Firebase operation that creates an account or sends mail.
 */
export function assertAuthEmailAllowed(email: string): void {
  const allowlist = getAllowlist();
  if (!allowlist || isAuthEmailAllowed(email)) return;

  console.warn(
    `[auth-email-guard] VITE_AUTH_EMAIL_ALLOWLIST active (${allowlist.join(', ')}): ` +
      `refused Firebase auth email for ${email}`,
  );
  throw new Error(
    `Blocked: this environment only allows auth email for addresses matching ${allowlist.join(', ')}. ` +
      `Use a test address instead of ${email}.`,
  );
}
